import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  Mic, MicOff, Send, RotateCcw, ChevronDown, Volume2,
  BookOpen, MessageSquare, CheckCircle, AlertCircle, Lightbulb, Target
} from 'lucide-react';
import apiClient from '../../helpers/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

type Part = 1 | 2 | 3;
type Tab = 'speaking' | 'writing';

interface SpeakingResult {
  overall_band: number;
  criteria: {
    fluency_coherence: { band: number; comment: string };
    lexical_resource: { band: number; comment: string };
    grammatical_range: { band: number; comment: string };
    pronunciation: { band: number; comment: string };
  };
  strengths: string[];
  weaknesses: string[];
  gap_to_target: string;
  improved_answer: string;
}

interface SentenceCorrection {
  original: string;
  corrected: string;
  reason: string;
}

interface WritingResult {
  overall_band: number;
  word_count: number;
  criteria: {
    task_achievement: { band: number; comment: string };
    coherence_cohesion: { band: number; comment: string };
    lexical_resource: { band: number; comment: string };
    grammatical_range: { band: number; comment: string };
  };
  strengths: string[];
  weaknesses: string[];
  gap_to_target: string;
  sentence_corrections: SentenceCorrection[];
  improved_outline: string;
  band_tips: string[];
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.06)}`;
const ring = keyframes`0%{transform:scale(1);opacity:.6}70%{transform:scale(2);opacity:0}100%{transform:scale(2);opacity:0}`;
const shimmer = keyframes`0%{background-position:-200% 0}100%{background-position:200% 0}`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 60px;
  animation: ${fadeUp} .4s ease;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: 24px;
  padding: 36px 32px;
  margin-bottom: 28px;
  text-align: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute; top: -60px; right: -60px;
    width: 200px; height: 200px; border-radius: 50%;
    background: radial-gradient(circle, rgba(102,126,234,.15), transparent 70%);
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(24px, 5vw, 36px);
  font-weight: 900;
  color: #fff;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
`;

const HeroSub = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin: 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 14px;
  border-radius: 16px;
  border: 2px solid ${p => p.$active ? 'transparent' : '#e2e8f0'};
  background: ${p => p.$active
    ? 'linear-gradient(135deg, #667eea, #764ba2)'
    : '#fff'};
  color: ${p => p.$active ? '#fff' : '#64748b'};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all .25s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${p => p.$active ? '0 8px 24px rgba(102,126,234,.35)' : '0 2px 8px rgba(0,0,0,.06)'};
  &:hover { transform: translateY(-1px); }
`;

const Card = styled.div<{ $glow?: string }>`
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 18px;
  box-shadow: 0 4px 24px rgba(0,0,0,.06);
  border: 1.5px solid #f1f5f9;
  position: relative;
  animation: ${fadeUp} .35s ease;
  ${p => p.$glow && css`border-color: ${p.$glow}40; box-shadow: 0 4px 24px ${p.$glow}20;`}
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const Select = styled.select`
  width: 100%;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  background: #f8fafc;
  outline: none;
  cursor: pointer;
  margin-bottom: 10px;
  &:focus { border-color: #667eea; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
  font-size: 14px;
  line-height: 1.65;
  color: #334155;
  resize: vertical;
  font-family: inherit;
  outline: none;
  background: #f8fafc;
  transition: border .2s;
  &:focus { border-color: #667eea; background: #fff; }
  &::placeholder { color: #94a3b8; }
`;

const TranscriptBox = styled.div`
  min-height: 80px;
  padding: 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1.5px dashed #e2e8f0;
  font-size: 14px;
  color: #334155;
  line-height: 1.65;
  white-space: pre-wrap;
`;

const MicArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 20px 0 10px;
`;

const MicWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Ring = styled.div`
  position: absolute;
  width: 80px; height: 80px;
  border-radius: 50%;
  background: rgba(239,68,68,.25);
  animation: ${ring} 1.3s ease-out infinite;
`;

const MicButton = styled.button<{ $recording: boolean }>`
  position: relative;
  z-index: 1;
  width: 76px; height: 76px;
  border-radius: 50%;
  border: none;
  background: ${p => p.$recording
    ? 'linear-gradient(135deg,#ef4444,#dc2626)'
    : 'linear-gradient(135deg,#667eea,#764ba2)'};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${p => p.$recording
    ? '0 8px 28px rgba(239,68,68,.5)'
    : '0 8px 28px rgba(102,126,234,.4)'};
  transition: background .25s, box-shadow .25s;
  animation: ${p => p.$recording ? pulse : 'none'} .9s ease infinite;
  &:hover { transform: scale(1.05); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const MicLabel = styled.div<{ $recording: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$recording ? '#ef4444' : '#667eea'};
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 20px rgba(102,126,234,.4);
  transition: all .2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(102,126,234,.45); }
  &:disabled { opacity: .6; cursor: not-allowed; transform: none; }
`;

const Loader = styled.div`
  width: 20px; height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} .8s linear infinite;
`;

// ─── Result styled ────────────────────────────────────────────────────────────

const BandBadge = styled.div<{ $band: number }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px; height: 100px;
  border-radius: 50%;
  background: ${p => p.$band >= 7 ? 'linear-gradient(135deg,#22c55e,#16a34a)'
    : p.$band >= 6 ? 'linear-gradient(135deg,#f59e0b,#d97706)'
    : 'linear-gradient(135deg,#ef4444,#dc2626)'};
  color: #fff;
  box-shadow: 0 8px 24px ${p => p.$band >= 7 ? 'rgba(34,197,94,.4)'
    : p.$band >= 6 ? 'rgba(245,158,11,.4)'
    : 'rgba(239,68,68,.4)'};
  animation: ${fadeUp} .5s ease;
  .num { font-size: 32px; font-weight: 900; line-height: 1; }
  .lbl { font-size: 11px; font-weight: 700; opacity: .85; margin-top: 2px; }
`;

const BandSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const BandMeta = styled.div`
  flex: 1;
  min-width: 200px;
  .title { font-size: 22px; font-weight: 800; color: #1e293b; margin-bottom: 6px; }
  .sub { font-size: 14px; color: #6b7280; }
`;

const CriteriaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
`;

const CriteriaCard = styled.div<{ $band: number }>`
  background: ${p => p.$band >= 7 ? '#f0fdf4' : p.$band >= 6 ? '#fffbeb' : '#fff5f5'};
  border: 1.5px solid ${p => p.$band >= 7 ? '#86efac' : p.$band >= 6 ? '#fde68a' : '#fca5a5'};
  border-radius: 14px;
  padding: 14px;
  .label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 6px; }
  .band-num { font-size: 26px; font-weight: 900; color: ${p => p.$band >= 7 ? '#16a34a' : p.$band >= 6 ? '#d97706' : '#dc2626'}; }
  .comment { font-size: 12px; color: #475569; margin-top: 8px; line-height: 1.5; }
`;

const ListSection = styled.div<{ $color: string }>`
  background: ${p => p.$color}10;
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
  .sect-title {
    font-size: 13px; font-weight: 700;
    color: ${p => p.$color};
    margin-bottom: 10px;
    display: flex; align-items: center; gap: 7px;
  }
  ul { margin: 0; padding-left: 18px; }
  li { font-size: 13px; color: #334155; margin-bottom: 6px; line-height: 1.5; }
`;

const ModelAnswer = styled.div`
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border: 1.5px solid #86efac;
  border-radius: 14px;
  padding: 18px;
  font-size: 14px;
  color: #15803d;
  line-height: 1.7;
  font-style: italic;
  .title { font-style: normal; font-weight: 700; color: #15803d; margin-bottom: 8px; display: flex; align-items: center; gap: 7px; }
`;

const CorrectionCard = styled.div`
  border: 1.5px solid #e0e7ff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
  .orig { font-size: 13px; color: #dc2626; text-decoration: line-through; margin-bottom: 4px; }
  .fixed { font-size: 13px; color: #16a34a; font-weight: 600; margin-bottom: 6px; }
  .reason { font-size: 12px; color: #6b7280; }
`;

const WordCount = styled.div<{ $ok: boolean }>`
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$ok ? '#16a34a' : '#94a3b8'};
  margin-top: 4px;
`;

const TipPill = styled.div`
  background: #eef2ff;
  border: 1.5px solid #c7d2fe;
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 13px;
  color: #4f46e5;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CRITERIA_LABELS: Record<string, string> = {
  fluency_coherence: 'Lưu loát & Mạch lạc',
  lexical_resource: 'Từ vựng',
  grammatical_range: 'Ngữ pháp',
  pronunciation: 'Phát âm',
  task_achievement: 'Nội dung & Nhiệm vụ',
  coherence_cohesion: 'Liên kết & Mạch lạc',
};

function getBandLabel(band: number): string {
  if (band >= 9) return 'Expert';
  if (band >= 8) return 'Very Good';
  if (band >= 7) return 'Good';
  if (band >= 6) return 'Competent';
  if (band >= 5) return 'Modest';
  return 'Limited';
}

// ─── Main Component ───────────────────────────────────────────────────────────

const IELTSPracticePage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('speaking');

  // Speaking state
  const [spQuestion, setSpQuestion] = useState('');
  const [spPart, setSpPart] = useState<Part>(1);
  const [spTarget, setSpTarget] = useState(7.5);
  const [spTranscript, setSpTranscript] = useState('');
  const [spInterim, setSpInterim] = useState('');
  const [spRecording, setSpRecording] = useState(false);
  const [spLoading, setSpLoading] = useState(false);
  const [spResult, setSpResult] = useState<SpeakingResult | null>(null);
  const [spError, setSpError] = useState('');
  const recognitionRef = useRef<any>(null);
  const accumulatedRef = useRef('');   // accumulates final transcript, avoids stale closure
  const shouldRestartRef = useRef(false); // auto-restart on silence

  // Writing state
  const [wrQuestion, setWrQuestion] = useState('');
  const [wrEssay, setWrEssay] = useState('');
  const [wrTarget, setWrTarget] = useState(7.5);
  const [wrLoading, setWrLoading] = useState(false);
  const [wrResult, setWrResult] = useState<WritingResult | null>(null);
  const [wrError, setWrError] = useState('');

  const SpeechAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const speechSupported = !!SpeechAPI;

  const wordCount = wrEssay.trim().split(/\s+/).filter(Boolean).length;

  // ── Speaking: Recording ────────────────────────────────────────────────────

  const stopRecording = useCallback(() => {
    shouldRestartRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setSpRecording(false);
    setSpInterim('');
  }, []);

  const startRecording = useCallback(() => {
    if (!speechSupported) return;
    shouldRestartRef.current = true;

    const launch = () => {
      if (!shouldRestartRef.current) return;
      const rec = new SpeechAPI();
      recognitionRef.current = rec;
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      rec.onresult = (e: any) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) {
            accumulatedRef.current += (accumulatedRef.current ? ' ' : '') + t.trim();
          } else {
            interim = t;
          }
        }
        setSpTranscript(accumulatedRef.current);
        setSpInterim(interim);
      };

      rec.onerror = (e: any) => {
        if (e.error === 'no-speech') return; // Chrome fires this on silence — handled by onend
        shouldRestartRef.current = false;
        setSpRecording(false);
        setSpInterim('');
      };

      // Chrome stops recognition after ~7–10s of silence; restart automatically
      rec.onend = () => {
        setSpInterim('');
        if (shouldRestartRef.current) {
          // Brief delay before restarting to avoid DOMException
          setTimeout(launch, 150);
        } else {
          setSpRecording(false);
        }
      };

      rec.start();
    };

    accumulatedRef.current = ''; // reset accumulator for new session
    setSpTranscript('');
    setSpInterim('');
    setSpRecording(true);
    launch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechSupported]);

  useEffect(() => () => { if (recognitionRef.current) try { recognitionRef.current.stop(); } catch {} }, []);

  // ── Speaking: Analyze ──────────────────────────────────────────────────────

  const analyzeSpeak = async () => {
    if (!spQuestion.trim() || !spTranscript.trim()) return;
    stopRecording();
    setSpLoading(true);
    setSpError('');
    setSpResult(null);
    try {
      const res = await apiClient.post('/api/ielts/speaking', {
        question: spQuestion,
        part: spPart,
        transcript: spTranscript,
        targetBand: spTarget,
      });
      if (res.data?.success) setSpResult(res.data.data);
      else setSpError(res.data?.message || 'Có lỗi xảy ra');
    } catch (e: any) {
      setSpError(e?.response?.data?.message || 'Không kết nối được server');
    } finally {
      setSpLoading(false);
    }
  };

  // ── Writing: Analyze ───────────────────────────────────────────────────────

  const analyzeWrite = async () => {
    if (!wrQuestion.trim() || !wrEssay.trim()) return;
    setWrLoading(true);
    setWrError('');
    setWrResult(null);
    try {
      const res = await apiClient.post('/api/ielts/writing', {
        question: wrQuestion,
        essay: wrEssay,
        targetBand: wrTarget,
      });
      if (res.data?.success) setWrResult(res.data.data);
      else setWrError(res.data?.message || 'Có lỗi xảy ra');
    } catch (e: any) {
      setWrError(e?.response?.data?.message || 'Không kết nối được server');
    } finally {
      setWrLoading(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderCriteria = (criteria: Record<string, { band: number; comment: string }>) => (
    <CriteriaGrid>
      {Object.entries(criteria).map(([key, val]) => (
        <CriteriaCard key={key} $band={val.band}>
          <div className="label">{CRITERIA_LABELS[key] || key}</div>
          <div className="band-num">Band {val.band}</div>
          <div className="comment">{val.comment}</div>
        </CriteriaCard>
      ))}
    </CriteriaGrid>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Page>
      <Hero>
        <HeroTitle>🎓 Luyện thi IELTS</HeroTitle>
        <HeroSub>AI chấm điểm & nhận xét theo thang IELTS chính thức · Target Band 7.5</HeroSub>
      </Hero>

      <TabRow>
        <TabBtn $active={tab === 'speaking'} onClick={() => setTab('speaking')}>
          <Mic size={18} /> Speaking
        </TabBtn>
        <TabBtn $active={tab === 'writing'} onClick={() => setTab('writing')}>
          <BookOpen size={18} /> Writing Task 2
        </TabBtn>
      </TabRow>

      {/* ══ SPEAKING TAB ════════════════════════════════════════════════════ */}
      {tab === 'speaking' && (
        <>
          <Card>
            <CardTitle><MessageSquare size={14} /> Câu hỏi Speaking</CardTitle>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <Select value={spPart} onChange={e => setSpPart(Number(e.target.value) as Part)} style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
                <option value={1}>Part 1 — Câu hỏi cá nhân</option>
                <option value={2}>Part 2 — Cue Card (nói dài)</option>
                <option value={3}>Part 3 — Thảo luận</option>
              </Select>
              <Select value={spTarget} onChange={e => setSpTarget(Number(e.target.value))} style={{ maxWidth: 160, marginBottom: 0 }}>
                {[5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9].map(b => (
                  <option key={b} value={b}>Target: Band {b}</option>
                ))}
              </Select>
            </div>
            <Textarea
              rows={4}
              placeholder="Paste câu hỏi IELTS Speaking vào đây..."
              value={spQuestion}
              onChange={e => setSpQuestion(e.target.value)}
            />
          </Card>

          <Card>
            <CardTitle><Mic size={14} /> Ghi âm câu trả lời</CardTitle>
            {!speechSupported ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#dc2626', fontSize: 14 }}>
                ⚠️ Trình duyệt không hỗ trợ ghi âm. Dùng Google Chrome nhé!
              </div>
            ) : (
              <>
                <MicArea>
                  <MicWrap>
                    {spRecording && <Ring />}
                    <MicButton $recording={spRecording} onClick={spRecording ? stopRecording : startRecording}>
                      {spRecording ? <MicOff size={30} /> : <Mic size={30} />}
                    </MicButton>
                  </MicWrap>
                  <MicLabel $recording={spRecording}>
                    {spRecording ? '🔴 Đang ghi âm — Nhấn để dừng' : '🎤 Nhấn để bắt đầu nói'}
                  </MicLabel>
                </MicArea>
                <TranscriptBox>
                  {spTranscript || <span style={{ color: '#94a3b8' }}>(Transcript sẽ hiện ở đây...)</span>}
                  {spInterim && <span style={{ color: '#94a3b8' }}> {spInterim}</span>}
                </TranscriptBox>
                {spTranscript && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      onClick={() => { setSpTranscript(''); setSpInterim(''); setSpResult(null); }}
                      style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', color: '#667eea', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                    >
                      <RotateCcw size={12} style={{ marginRight: 4 }} />Xoá
                    </button>
                  </div>
                )}
              </>
            )}
          </Card>

          <PrimaryBtn
            onClick={analyzeSpeak}
            disabled={spLoading || !spQuestion.trim() || !spTranscript.trim()}
          >
            {spLoading ? <><Loader /> Đang phân tích AI...</> : <><Send size={16} /> Phân tích & Chấm điểm</>}
          </PrimaryBtn>

          {spError && (
            <Card $glow="#ef4444" style={{ marginTop: 16 }}>
              <div style={{ color: '#dc2626', fontWeight: 600, fontSize: 14 }}>❌ {spError}</div>
            </Card>
          )}

          {spResult && (
            <Card $glow="#667eea" style={{ marginTop: 20 }}>
              <BandSummary>
                <BandBadge $band={spResult.overall_band}>
                  <span className="num">{spResult.overall_band}</span>
                  <span className="lbl">BAND</span>
                </BandBadge>
                <BandMeta>
                  <div className="title">{getBandLabel(spResult.overall_band)}</div>
                  <div className="sub">Overall Band Score · Speaking</div>
                  <div style={{ marginTop: 8, fontSize: 13, color: '#667eea', fontWeight: 600 }}>
                    🎯 {spResult.gap_to_target}
                  </div>
                </BandMeta>
              </BandSummary>

              {renderCriteria(spResult.criteria as any)}

              <ListSection $color="#22c55e">
                <div className="sect-title"><CheckCircle size={14} /> Điểm mạnh</div>
                <ul>{spResult.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </ListSection>

              <ListSection $color="#ef4444">
                <div className="sect-title"><AlertCircle size={14} /> Điểm cần cải thiện</div>
                <ul>{spResult.weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </ListSection>

              <ModelAnswer>
                <div className="title"><Lightbulb size={15} /> Câu trả lời mẫu (Band {spTarget}+)</div>
                {spResult.improved_answer}
              </ModelAnswer>
            </Card>
          )}
        </>
      )}

      {/* ══ WRITING TAB ════════════════════════════════════════════════════ */}
      {tab === 'writing' && (
        <>
          <Card>
            <CardTitle><BookOpen size={14} /> Đề bài Writing Task 2</CardTitle>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <Select value={wrTarget} onChange={e => setWrTarget(Number(e.target.value))} style={{ maxWidth: 180, marginBottom: 0 }}>
                {[5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9].map(b => (
                  <option key={b} value={b}>Target: Band {b}</option>
                ))}
              </Select>
            </div>
            <Textarea
              rows={5}
              placeholder="Paste đề bài IELTS Writing Task 2 vào đây..."
              value={wrQuestion}
              onChange={e => setWrQuestion(e.target.value)}
            />
          </Card>

          <Card>
            <CardTitle><MessageSquare size={14} /> Bài viết của bạn</CardTitle>
            <Textarea
              rows={14}
              placeholder="Viết bài luận của bạn vào đây (tối thiểu 250 từ)..."
              value={wrEssay}
              onChange={e => setWrEssay(e.target.value)}
            />
            <WordCount $ok={wordCount >= 250}>
              {wordCount} từ {wordCount >= 250 ? '✅' : `(cần thêm ${250 - wordCount} từ)`}
            </WordCount>
          </Card>

          <PrimaryBtn
            onClick={analyzeWrite}
            disabled={wrLoading || !wrQuestion.trim() || wordCount < 50}
          >
            {wrLoading ? <><Loader /> Đang chấm bài AI...</> : <><Send size={16} /> Nộp bài & Chấm điểm</>}
          </PrimaryBtn>

          {wrError && (
            <Card $glow="#ef4444" style={{ marginTop: 16 }}>
              <div style={{ color: '#dc2626', fontWeight: 600, fontSize: 14 }}>❌ {wrError}</div>
            </Card>
          )}

          {wrResult && (
            <Card $glow="#667eea" style={{ marginTop: 20 }}>
              <BandSummary>
                <BandBadge $band={wrResult.overall_band}>
                  <span className="num">{wrResult.overall_band}</span>
                  <span className="lbl">BAND</span>
                </BandBadge>
                <BandMeta>
                  <div className="title">{getBandLabel(wrResult.overall_band)}</div>
                  <div className="sub">Overall Band Score · Writing Task 2 · {wrResult.word_count} từ</div>
                  <div style={{ marginTop: 8, fontSize: 13, color: '#667eea', fontWeight: 600 }}>
                    🎯 {wrResult.gap_to_target}
                  </div>
                </BandMeta>
              </BandSummary>

              {renderCriteria(wrResult.criteria as any)}

              <ListSection $color="#22c55e">
                <div className="sect-title"><CheckCircle size={14} /> Điểm mạnh</div>
                <ul>{wrResult.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </ListSection>

              <ListSection $color="#ef4444">
                <div className="sect-title"><AlertCircle size={14} /> Điểm cần cải thiện</div>
                <ul>{wrResult.weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </ListSection>

              {wrResult.sentence_corrections?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <CardTitle style={{ marginBottom: 10 }}><AlertCircle size={14} /> Sửa câu cụ thể</CardTitle>
                  {wrResult.sentence_corrections.map((c, i) => (
                    <CorrectionCard key={i}>
                      <div className="orig">❌ {c.original}</div>
                      <div className="fixed">✅ {c.corrected}</div>
                      <div className="reason">💡 {c.reason}</div>
                    </CorrectionCard>
                  ))}
                </div>
              )}

              <ModelAnswer>
                <div className="title"><Target size={15} /> Gợi ý outline Band {wrTarget}+</div>
                <div style={{ fontStyle: 'normal', color: '#15803d', whiteSpace: 'pre-line' }}>{wrResult.improved_outline}</div>
              </ModelAnswer>

              {wrResult.band_tips?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <CardTitle style={{ marginBottom: 10 }}><Lightbulb size={14} /> Tips tăng band điểm</CardTitle>
                  {wrResult.band_tips.map((tip, i) => (
                    <TipPill key={i}>💡 {tip}</TipPill>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </Page>
  );
};

export default IELTSPracticePage;
